import React from 'react';
import { FIELD_BASE_CLASS, FIELD_CLASS } from './ui/fieldStyles';
import Button from './ui/Button';

type DashboardPaginationProps = {
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  itemLabel?: string;
};

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50];

const DashboardPagination: React.FC<DashboardPaginationProps> = ({
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  itemLabel = 'itens',
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const startItem = totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const endItem = Math.min(safePage * pageSize, totalItems);

  const pages: number[] = [];
  const startPage = Math.max(1, safePage - 2);
  const endPage = Math.min(totalPages, startPage + 4);

  for (let current = startPage; current <= endPage; current += 1) {
    pages.push(current);
  }

  return (
    <div className="flex flex-col gap-3 border-t border-slate-200 px-4 py-4 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-col gap-2 text-sm text-slate-500 dark:text-slate-400 sm:flex-row sm:items-center sm:gap-4">
        <span>
          Mostrando <span className="font-semibold text-slate-700 dark:text-slate-200">{startItem}</span> a{' '}
          <span className="font-semibold text-slate-700 dark:text-slate-200">{endItem}</span> de{' '}
          <span className="font-semibold text-slate-700 dark:text-slate-200">{totalItems}</span> {itemLabel}
        </span>
        {onPageSizeChange && (
          <label className="flex items-center gap-2">
            <span>Por página</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className={FIELD_BASE_CLASS}
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Button variant="secondary" onClick={() => onPageChange(safePage - 1)} disabled={safePage <= 1}>
          Anterior
        </Button>
        <div className="flex items-center gap-1">
          {pages.map((pageNumber) => (
            <Button
              key={pageNumber}
              variant={pageNumber === safePage ? 'primary' : 'secondary'}
              className="min-w-10 !px-2"
              aria-current={pageNumber === safePage ? 'page' : undefined}
              onClick={() => onPageChange(pageNumber)}
            >
              {pageNumber}
            </Button>
          ))}
        </div>
        <Button variant="secondary" onClick={() => onPageChange(safePage + 1)} disabled={safePage >= totalPages}>
          Próxima
        </Button>
      </div>
    </div>
  );
};

export default DashboardPagination;
